/**
 * Utility to register a microfrontend for loading modules from the same agnostic of the environment.
 *
 * Prerequisite to consume a microfrontend is the inclusion of the following scripts in the consumer application.
 * https://workstation.morningstar.com/microfrontends/mfe-core/main.js
 * https://workstation.morningstar.com/microfrontends/workstation-integration/main.js
 *
 * For example, let's say there is a microfrontend built and deployed to the following location:
 * https://workstation-qa.morningstar.com/microfrontends/awse.user-info/remoteEntry.js
 *
 * To register this microfrontend, the usage looks like this:
 * ```js
 * registerFederatedModule("awse.user-info", "user_info")
 * ```
 *
 * Since all microfrontends are deployed under the "microfrontends" folder, we can use this utility to
 * register the entry point by providing the folder name within and the unique namespace identifier defined by the microfrontend.
 * The namespace identifier for a microfrontend can be looked up in two ways:
 * 1. Source code: See the property "name" in the file "configuration.json".
 * 2. Hosted deployment: See the property "name" in the descriptor file. Ex: https://workstation-qa.morningstar.com/microfrontends/awse.user-info/metadata.json.
 *
 * @param {string} path Folder name that indicates the microfrontend of interest.
 * @param {string} identifier Namespace that is defined by the microfrontend of interest. See "configuration.json" file inside the microfrontend codebase.
 */
export function registerFederatedModule(path, identifier) {
    window.__module_federation__.registerRemote(
        `${window.__awse_mfe__.MFE_PROVIDER}/microfrontends/${path}/remoteEntry.js?when=${new Date().getTime()}`,
        identifier
    );
}

/**
 * Utility to load a module from a hosted microfrontend lazily at runtime.
 *
 * Here are a few examples.
 *
 * To register a Vue component exposed from a federated module, the usage looks like this:
 * ```js
 * export default {
 *  components: {
 *    LocalComponent: () => import("../../some/folder/LocalComponent"),
 *    RemoteComponent: () => importFromFederatedModule("user_info/some/path/GridComponent")
 *  }
 * }
 * ```
 *
 * To load a utility method exposed from a federated module, the usage looks like this:
 * ```js
 * async funtion calculateRiskScore(age, income, investMode) {
 *    // load the logic/algorithm from a federated module
 *    const computeRiskScoreFn = await importFromFederatedModule("user_info/some/path/to/fns/risk-score");
 *    const mode = investMode ? 'aggressive' : 'moderate';
 *    return computeRiskScoreFn(age, mode, income);
 * }
 * ```
 *
 * For more details around module federation, please see https://mswiki.morningstar.com/display/FINPLAN/Micro+Frontends+and+Module+Federation%3A+What%2C+Why+and+When.
 *
 * @param {string} moduleLocator Path to the module of interest. Ex: "import_mapping"
 * @returns {Promise<T>} Promise to resolve the requested module
 */
export async function importFromFederatedModule(moduleLocator) {
    return window.__module_federation__.lazyImport(moduleLocator);
}

export function setup() {
    const modules = [
        ["awse-mwc/awse_core", "awse_core"],
        ["awse-mwc/awse_portfolio_construction_tool", "awse_portfolio_construction_tool"]
    ];

    modules.forEach(([path, name]) => registerFederatedModule(path, name));
}

export function useCdnAsset(microfrontend, resource) {
    return window.awse_cdn?.[microfrontend]?.[resource];
}
